/**
 * CR305+CR306: Recipes Studio — Layout Studio-style editor for Web Crawl recipes
 */

import { AdminLayout } from '@/components/admin/AdminLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useRecipesStudio } from '@/hooks/useRecipesStudio';
import {
  RecipePickerBar,
  RecipeBasicsSection,
  RecipeStartUrlSection,
  RecipeRenderingSection,
  RecipeConsentSection,
  RecipeOfferSafeSection,
  RecipeActionsSection,
  RecipeDetailPanel,
} from '@/components/admin/recipes-studio';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Recipe } from '@/lib/recipes-studio/types';

const RecipesStudio = () => {
  const { isAdmin } = useAuth();
  const {
    recipes,
    selectedId,
    selectedRecipe,
    isLoading,
    saving,
    scopeFilter,
    setScopeFilter,
    setSelectedId,
    createRecipe,
    cloneRecipe,
    archiveRecipe,
    updateRecipe,
  } = useRecipesStudio('WEB_CRAWL');

  const handleSave = (updates: Partial<Recipe>) => {
    if (!selectedId) return;
    updateRecipe(selectedId, updates);
  };

  const handleManageRecipes = () => {
    toast.info('Manage All Recipes — coming in a future CR');
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Admin access required</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-4rem)] flex flex-col">
        {/* Top Bar: Recipe Picker */}
        <RecipePickerBar
          recipes={recipes}
          selectedId={selectedId}
          scopeFilter={scopeFilter}
          onSelectRecipe={setSelectedId}
          onCreateNew={(name, fromTemplateId) => createRecipe(name, fromTemplateId)}
          onClone={() => selectedId && cloneRecipe(selectedId)}
          onArchive={() => selectedId && archiveRecipe(selectedId)}
          onManageRecipes={handleManageRecipes}
          onScopeChange={setScopeFilter}
        />

        {/* Main Content: Split Pane */}
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="flex-1">
            {/* Left Panel: Settings Sections */}
            <ResizablePanel defaultSize={40} minSize={30} maxSize={55}>
              <ScrollArea className="h-full">
                <div className="p-4 space-y-4">
                  {selectedRecipe ? (
                    <>
                      <RecipeBasicsSection
                        recipe={selectedRecipe}
                        saving={saving}
                        onSave={handleSave}
                      />
                      <RecipeStartUrlSection
                        recipe={selectedRecipe}
                        saving={saving}
                        onSave={handleSave}
                      />
                      <RecipeRenderingSection
                        recipe={selectedRecipe}
                        saving={saving}
                        onSave={handleSave}
                      />
                      <RecipeConsentSection
                        recipe={selectedRecipe}
                        saving={saving}
                        onSave={handleSave}
                      />
                      <RecipeOfferSafeSection
                        recipe={selectedRecipe}
                        saving={saving}
                        onSave={handleSave}
                      />
                      <RecipeActionsSection
                        recipe={selectedRecipe}
                        saving={saving}
                        onSave={handleSave}
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                      Create or select a recipe to begin editing
                    </div>
                  )}
                </div>
              </ScrollArea>
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Right Panel: Details + Test Results */}
            <ResizablePanel defaultSize={60}>
              <RecipeDetailPanel recipe={selectedRecipe} />
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>
    </AdminLayout>
  );
};

export default RecipesStudio;
